import { Fragment, useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import ABTestLabelBox, { getSendLabel } from 'domains/campaigns/components/ABTestLabelBox';
import ABTestSlider from 'domains/campaigns/components/ABTestSlider';
import {
    Campaign,
    CampaignChannelLabels,
    CampaignChannels,
    Send,
    SendRecurrenceFrequency,
    SplitActions,
    SplitTypes,
    ABTestType,
} from 'domains/campaigns/types';
import { findDoesNotMeetSibling, InternalSend, isSplitBranchRoot } from 'domains/campaigns/utils/buildCampaignGraph';
import groupSendsByParentId from 'domains/campaigns/utils/groupSendsByParentId';
import inferExitSendParentIds from 'domains/campaigns/utils/inferExitSendParentIds';
import synthesizeSplitNodeIds from 'domains/campaigns/utils/synthesizeSplitNodeIds';
import ContentDetail from 'domains/content/components/ContentDetail';
import { VoiceCallerDetailById } from 'domains/content/components/ContentDetail/ContentDetail';
import ButtonContentPreview from 'domains/core/components/AutocompleteContent/ButtonContentPreview';
import { formatDateTime } from 'domains/core/components/DateRangeText/DateRangeText';
import DetailItem from 'domains/core/components/DetailItem';
import SegmentDetailsDisplay from 'domains/segments/pages/SegmentsPage/SegmentsTable/SegmentDetails/SegmentDetailsDisplay';
import useAppSendSenderProfileTemplate from 'hooks/queries/useAppSendSenderProfileTemplate';
import useSegment from 'hooks/queries/useSegment';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import { CampaignStatus } from 'models/enums';

// Mapping for A/B Test types to display-friendly labels
const AB_TEST_TYPE_LABELS = {
    [ABTestType.CONTENT]: 'content',
    [ABTestType.SEND_TIME]: 'send time',
} as const;

// `isSplitBranchRoot`/`findDoesNotMeetSibling` only read id, parentId, splitType, splitNodeId,
// segmentId, and channel. `Send` natively has all of those except `splitNodeId` — that one is
// synthesized onto these objects at runtime by `synthesizeSplitNodeIds` (called above), not a
// native `Send` field. `_index` is the only field genuinely absent, and is only consulted as a
// fallback key when `.id` is null, never true for a published send. Safe to treat as an
// `InternalSend` for tree-walk purposes only.
const asInternalSend = (send: Send): InternalSend => (send as unknown) as InternalSend;

type ConditionalSplitProps = {
    allSends: InternalSend[];
    send: Send;
};

const ConditionalSplitDetailItem = ({ allSends, send }: ConditionalSplitProps) => {
    // This renders before a true "meets criteria" branch root. If the "meets criteria" action is
    // exit, we will try to use the criteria from its "does not meet criteria" sibling (found by
    // parentId/splitNodeId, not array position — nested/edited trees aren't guaranteed adjacent).
    // If, for some reason, the sibling action is _also_ exit, there will be no criteria to show.
    const dnmSibling = findDoesNotMeetSibling(asInternalSend(send), allSends);

    // Current flow writes criteria directly onto the send as a nested `segmentDefinition` — no
    // saved `Segment` row is created, so this is the primary source. `segmentId` is a legacy
    // fallback for splits created before that change, which do still reference a saved segment.
    const segmentDefinition = send?.segmentDefinition || dnmSibling?.segmentDefinition;
    const splitSegmentId = send?.segmentId || dnmSibling?.segmentId;
    const { data: segmentData } = useSegment(splitSegmentId, {
        enabled: !segmentDefinition && !!splitSegmentId,
    });

    const resolvedDefinition = segmentDefinition || segmentData?.segmentDefinition;

    return (
        <DetailItem
            header="Conditional Split"
            description={
                resolvedDefinition ? (
                    <SegmentDetailsDisplay segmentDefinition={resolvedDefinition} />
                ) : (
                    'no split description available'
                )
            }
        />
    );
};

type SendProps = {
    contentPreviewNodeId: string | number;
    handlePreviewClick: (id: number | null) => void;
    recurrenceFrequency: SendRecurrenceFrequency;
    send: Send;
    index: number;
    isSendTimeABTest?: boolean;
    isChildOfSplit?: boolean;
    hasNestedSplitsFlag?: boolean;
    isBranchRoot?: boolean;
};

const SendDetailItem = ({
    contentPreviewNodeId,
    handlePreviewClick,
    recurrenceFrequency,
    send,
    index,
    isSendTimeABTest,
    isChildOfSplit = false,
    hasNestedSplitsFlag = false,
    isBranchRoot = false,
}: SendProps) => {
    const { channel } = send;

    // Only a true branch ROOT gets the "Meets/Does Not Meet Criteria" heading — a chained send
    // deeper in the same branch inherits `splitType` from its branch (by design, see
    // customCampaignReducer's ADD_BRANCH_SEND) but is not itself a split boundary.
    const getHeader = () => {
        const sendChannel = CampaignChannelLabels[send?.channel];
        if (!isBranchRoot) return sendChannel;
        if (send.splitType === SplitTypes.MEETS) return 'Meets Criteria';
        return 'Does Not Meet Criteria';
    };
    const header = getHeader();
    // Legacy (flag OFF) blanks the header for nested sends; the new flow always shows it
    // because depth indentation makes the nesting clear without dropping the label.
    const headerValue = !hasNestedSplitsFlag && isChildOfSplit ? '' : header;

    function getContentId(channel: CampaignChannels, send: Send): number {
        switch (channel) {
            case CampaignChannels.DIRECT_MAIL:
                return send.directMailContentId;
            case CampaignChannels.SMS:
                return send.smsContentId || send.id;
            case CampaignChannels.VOICE_CALL:
                return send.voiceContentId;
            default:
                return send.id;
        }
    }

    if (send.splitAction === SplitActions.EXIT) {
        return <DetailItem key={`${send.splitType}-${index}`} header={header} description="exit curation" />;
    }

    const contentId = getContentId(channel, send);
    const isPreviewOpen = contentPreviewNodeId && send?.id === contentPreviewNodeId;
    const onClick = () => handlePreviewClick(isPreviewOpen ? null : send.id);

    if (channel === CampaignChannels.VOICE_CALL) {
        return (
            <>
                <ContentDetail
                    key={index}
                    channel={channel}
                    contentId={contentId}
                    firstEmailRecurrenceFrequency={recurrenceFrequency}
                    header={headerValue}
                    isFirstEmail={index === 0}
                    isPreviewOpen={isPreviewOpen}
                    recurrenceFrequency={send.recurrenceFrequency}
                    scheduledEndTimestamp={send.scheduledEndTimestamp}
                    scheduledTimestamp={send.scheduledTimestamp}
                    timeDelay={send.parentAudienceDelayInterval}
                    isSendTimeABTest={isSendTimeABTest}
                    onClick={onClick}
                />
                <VoiceCallerDetailById
                    callerId={send.voiceSenderProfileId}
                    isPreviewOpen={isPreviewOpen}
                ></VoiceCallerDetailById>
            </>
        );
    }

    const isSmsLegacy = channel === CampaignChannels.SMS && !send.smsContentId;

    return (
        <ContentDetail
            key={index}
            channel={channel}
            contentId={contentId}
            firstEmailRecurrenceFrequency={recurrenceFrequency}
            header={headerValue}
            isCampaign
            isFirstEmail={index === 0}
            isPreviewOpen={isPreviewOpen}
            isSmsLegacy={isSmsLegacy}
            recurrenceFrequency={send.recurrenceFrequency}
            scheduledEndTimestamp={send.scheduledEndTimestamp}
            scheduledTimestamp={send.scheduledTimestamp}
            timeDelay={send.parentAudienceDelayInterval}
            isSendTimeABTest={isSendTimeABTest}
            onClick={onClick}
        />
    );
};

// Component for Content A/B Testing Email display
type ContentABTestEmailsProps = {
    sends: Send[];
    contentPreviewId?: string | number;
    handlePreviewClick: (contentNodeId: number) => void;
    channel: CampaignChannels;
    winnerSendId?: number;
};

const ContentABTestEmails = ({
    sends,
    contentPreviewId,
    handlePreviewClick,
    channel,
    winnerSendId,
}: ContentABTestEmailsProps) => (
    <DetailItem
        header="Email"
        description={
            <Stack spacing={1}>
                {sends.slice(0, 2).map((send, index) => {
                    const label = getSendLabel(index);
                    const isPreviewOpen = contentPreviewId && send?.id === contentPreviewId;
                    const onClick = () => handlePreviewClick(isPreviewOpen ? null : send.id);
                    const isWinner = winnerSendId && send.id === winnerSendId;

                    return (
                        <EmailABTestItem
                            key={`email-ab-${index}`}
                            send={send}
                            label={label}
                            index={index}
                            isPreviewOpen={isPreviewOpen}
                            onClick={onClick}
                            channel={channel}
                            isWinner={isWinner}
                        />
                    );
                })}
            </Stack>
        }
    />
);

// Winner indicator chip component
const WinnerIndicator = () => (
    <Typography
        component="span"
        variant="caption"
        sx={{
            color: 'success.main',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 600,
        }}
    >
        (winner)
    </Typography>
);

// Individual Email A/B Test Item
type EmailABTestItemProps = {
    send: Send;
    label: string;
    index: number;
    isPreviewOpen: boolean;
    onClick: () => void;
    channel: CampaignChannels;
    isWinner?: boolean;
};

const EmailABTestItem = ({ send, label, index, isPreviewOpen, onClick, channel, isWinner }: EmailABTestItemProps) => {
    const { data: contentDetail, isSuccess: isContentDetailSuccess } = useAppSendSenderProfileTemplate(send.id, {
        enabled: Boolean(send.id),
    });

    const getEmailName = () => {
        if (!isContentDetailSuccess || !contentDetail) return `Email ${label}`;
        return contentDetail.templateName || `Email ${label}`;
    };

    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
                <ABTestLabelBox label={label} size="small" />
                <Typography variant="body1">{getEmailName()}</Typography>
            </Stack>
            <ButtonContentPreview contentType={channel} isActive={isPreviewOpen} onClick={onClick} />
            {isWinner && <WinnerIndicator />}
        </Stack>
    );
};

type CustomCampaignDetailsProps = {
    campaign: Campaign;
    contentPreviewNodeId?: string | number;
    handlePreviewClick: (contentNodeId: number) => void;
    hasNestedSplitsFlag?: boolean;
};

const CustomCampaignDetails = ({
    campaign,
    contentPreviewNodeId,
    handlePreviewClick,
    hasNestedSplitsFlag = false,
}: CustomCampaignDetailsProps) => {
    const { canCurationsAbTesting } = useAccessCentrePermissions();

    // Check A/B testing types for EMAIL channel only (suppress A/B layout when user lacks Access Centre permission)
    const isContentABTest =
        canCurationsAbTesting &&
        campaign.abTestConfig?.testOn === ABTestType.CONTENT &&
        campaign.channel === CampaignChannels.EMAIL;
    const isSendTimeABTest =
        canCurationsAbTesting &&
        campaign.abTestConfig?.testOn === ABTestType.SEND_TIME &&
        campaign.channel === CampaignChannels.EMAIL;

    const sendsWithFixedExits = useMemo(() => inferExitSendParentIds(campaign.sends ?? []), [campaign.sends]);

    // Published campaigns reconstruct topology from `campaign.structure`, which doesn't carry
    // `splitNodeId` — backfill it (same helper the graph nav already uses) so branch-root
    // detection below works at every depth, not just the first split.
    const synthesizedSends = useMemo(() => synthesizeSplitNodeIds(sendsWithFixedExits), [sendsWithFixedExits]);

    // Determine which sends to show in the main content area
    const firstSend = synthesizedSends?.[0];
    const sendsToShow = useMemo(() => (isSendTimeABTest && firstSend ? [firstSend] : synthesizedSends), [
        isSendTimeABTest,
        firstSend,
        synthesizedSends,
    ]);
    // Same list, viewed as InternalSend[] for the shared tree-walk helpers (isSplitBranchRoot /
    // findDoesNotMeetSibling) — see asInternalSend for why this cast is safe.
    const allSends = sendsToShow.map(asInternalSend);

    const groupedSends = useMemo(() => groupSendsByParentId(sendsToShow), [sendsToShow]);

    // Recursively render a send and its descendants. visitedIds guards against circular parentId chains (stack overflow).
    const renderSendGroup = (
        send: Send,
        children: Send[],
        parentIndex: number,
        depth: number = 0,
        parentHasSplit: boolean = false,
        visitedIds: Set<string | number> = new Set()
    ) => {
        const nodeKey = send?.id ?? `idx-${parentIndex}`;
        if (visitedIds.has(nodeKey)) return null;
        const nextVisited = new Set(visitedIds);
        nextVisited.add(nodeKey);

        const groupKey = `send-group-${send.id || parentIndex}`;
        // True only when `send` opens/closes a split boundary — not for a chained descendant
        // that merely inherited its branch's splitType. See asInternalSend for the cast note.
        const isBranchRoot = isSplitBranchRoot(asInternalSend(send), allSends);

        const content = (
            <>
                {/* Show conditional split header before a true "meets criteria" branch root */}
                {isBranchRoot && send.splitType === SplitTypes.MEETS && (
                    <ConditionalSplitDetailItem allSends={allSends} send={send} />
                )}

                {/* Render the parent send details */}
                <SendDetailItem
                    key={`send-${send.id || parentIndex}`}
                    contentPreviewNodeId={contentPreviewNodeId}
                    handlePreviewClick={handlePreviewClick}
                    index={parentIndex}
                    recurrenceFrequency={firstSend?.recurrenceFrequency}
                    send={send}
                    isSendTimeABTest={isSendTimeABTest}
                    isChildOfSplit={parentHasSplit}
                    hasNestedSplitsFlag={hasNestedSplitsFlag}
                    isBranchRoot={isBranchRoot}
                />

                {/* Recursively render each child and its descendants */}
                {children.map((child) => {
                    const childGroup = groupedSends.find((g) => g.parent === child);
                    const childIndex = childGroup?.parentIndex ?? sendsToShow.indexOf(child);
                    const grandchildren = childGroup?.children || [];
                    const childIsBranchRoot = isSplitBranchRoot(asInternalSend(child), allSends);
                    const nextDepth = childIsBranchRoot ? depth + 1 : depth;
                    // `parentHasSplit` only feeds the legacy (flag-off) header-blanking below —
                    // preserved as `!!send.splitType` to avoid changing that unrelated, out-of-scope path.
                    return renderSendGroup(child, grandchildren, childIndex, nextDepth, !!send.splitType, nextVisited);
                })}
            </>
        );

        // New flow (flag ON): indent nested splits via a Box; legacy flow keeps a flat Fragment.
        return hasNestedSplitsFlag ? (
            <Box
                key={groupKey}
                data-testid={depth > 0 ? `send-group-depth-${depth}` : undefined}
                sx={{ pl: depth * 2 }}
            >
                {content}
            </Box>
        ) : (
            <Fragment key={groupKey}>{content}</Fragment>
        );
    };

    return (
        <>
            {!isContentABTest &&
                groupedSends
                    .filter(
                        (group) => !group.parent.parentId || !sendsToShow.some((s) => s.id === group.parent.parentId)
                    )
                    .map((group) => renderSendGroup(group.parent, group.children, group.parentIndex))}
            {/* Show Content A/B Testing Email section */}
            {canCurationsAbTesting && isContentABTest && campaign.sends?.length >= 2 && (
                <>
                    <ContentABTestEmails
                        sends={campaign.sends}
                        contentPreviewId={contentPreviewNodeId}
                        handlePreviewClick={handlePreviewClick}
                        // AB testing is email-only (backend's validateAbTestConfig rejects
                        // non-email sends), so campaign.channel here is never 'MIXED'.
                        channel={campaign.channel as CampaignChannels}
                        winnerSendId={campaign.abTestConfig?.winnerSendId}
                    />
                </>
            )}

            {canCurationsAbTesting && campaign.abTestConfig && (
                <>
                    <DetailItem
                        header="A/B Testing"
                        description={AB_TEST_TYPE_LABELS[campaign.abTestConfig.testOn as ABTestType]}
                    />
                    <DetailItem
                        header="Test Size"
                        description={campaign.abTestConfig.testPercentage + '% audience'}
                        footer={
                            <>
                                <ABTestSlider
                                    segmentId={campaign.sends?.[0]?.segmentId}
                                    splitPercentage={campaign.abTestConfig.testPercentage as 50 | 100}
                                    campaignStatus={campaign.status}
                                />
                                {campaign.abTestConfig.testPercentage === 50 && (
                                    <Stack spacing={0} sx={{ marginTop: 1 }}>
                                        {campaign.abTestConfig.testEndsOn && (
                                            <Typography variant="body2" color="text.secondary">
                                                Test ends on {formatDateTime(campaign.abTestConfig.testEndsOn)}
                                            </Typography>
                                        )}
                                    </Stack>
                                )}
                            </>
                        }
                    />
                </>
            )}

            {/* Show single schedule for Content A/B testing */}
            {canCurationsAbTesting && isContentABTest && (
                <DetailItem
                    header="Scheduled"
                    description={
                        <Stack spacing={0.5}>
                            <Typography variant="body1">{formatDateTime(firstSend?.scheduledTimestamp)}</Typography>
                            {/* Winner send information */}
                            {campaign.abTestConfig?.testPercentage === 50 && (
                                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                                    {!campaign.sends?.[2]
                                        ? 'Winner will be manually sent to remaining 50%'
                                        : campaign.status === CampaignStatus.WINNER_SCHEDULED
                                        ? `Winner will be sent on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : campaign.status === CampaignStatus.WINNER_SENT
                                        ? `Winner sent Manually on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : 'Winner will be manually sent to remaining 50%'}
                                </Typography>
                            )}
                        </Stack>
                    }
                />
            )}

            {/* Show Scheduled section for send time A/B testing */}
            {canCurationsAbTesting && isSendTimeABTest && campaign.sends?.length >= 2 && (
                <DetailItem
                    header="Scheduled"
                    description={
                        <Stack spacing={0.5}>
                            {campaign.sends.slice(0, 2).map((send, index) => {
                                const label = getSendLabel(index);
                                const isWinner =
                                    campaign.abTestConfig?.winnerSendId &&
                                    send.id === campaign.abTestConfig.winnerSendId;
                                return (
                                    <Stack key={`schedule-${index}`} direction="row" alignItems="center" spacing={1}>
                                        <ABTestLabelBox label={label} size="small" />
                                        <Typography variant="body1">
                                            {formatDateTime(send?.scheduledTimestamp)}
                                        </Typography>
                                        {isWinner && <WinnerIndicator />}
                                    </Stack>
                                );
                            })}
                            {/* Winner send information */}
                            {campaign.abTestConfig?.testPercentage === 50 && (
                                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                                    {!campaign.sends?.[2]
                                        ? 'Winner will be manually sent to remaining 50%'
                                        : campaign.status === CampaignStatus.WINNER_SCHEDULED
                                        ? `Winner will be sent on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : campaign.status === CampaignStatus.WINNER_SENT
                                        ? `Winner sent Manually on ${formatDateTime(
                                              campaign.sends?.[2]?.scheduledTimestamp
                                          )}`
                                        : 'Winner will be manually sent to remaining 50%'}
                                </Typography>
                            )}
                        </Stack>
                    }
                />
            )}
        </>
    );
};

export default CustomCampaignDetails;
